/**
 * A character's name (ADR-065 decisions 1, 2 and 9, read through ADR-064 decision 4): two parts, the first
 * name, which is the character's own, and the family name, which is the account's, each unique across the game
 * ignoring case (a first name across the game by ADR-065's amendment of 2026-10-03, which suspends decision 4's
 * rule that a family alone holds it). The one definition of each part's rule, of the
 * family names the Story keeps, of the full name the page shows and of the name step's `needs`, read by the
 * gateway's name step, the login screen and the health probe.
 *
 * Each part takes 3 to 12 of `A-Za-z0-9_-` (the first name's cap of 12 by ADR-064's amendment of 2026-10-03; the
 * family name's rule waits on ADR-065's question 2), so two parts at the cap in the widest glyphs, with the space,
 * span 195 logical px of the pixel font, and every part stays inside the session token's 1 to 32 characters and
 * the font's printable ASCII. The limits and the character class below are the knob. The column `characters.name`
 * carries the first-name rule as one validated check (migration 020 adds `characters_name_cap`, migration 038
 * validates it and drops the rule before families, 3 to 24, of migration 007), which every write meets.
 */

/**
 * The characters a part of a name may hold, as the body of a character class.
 */
const PART_CHARACTERS = "A-Za-z0-9_-";

/**
 * One character a part of a name may hold.
 */
const NAME_CHARACTER_PATTERN = new RegExp(`^[${PART_CHARACTERS}]$`);

/**
 * Shortest first name accepted.
 */
export const MIN_FIRST_NAME_CHARS = 3;

/**
 * Longest first name accepted.
 */
export const MAX_FIRST_NAME_CHARS = 12;

/**
 * The first-name rule as pattern text, anchored. The same text is the `characters_name_cap` check on
 * `characters.name` (migrations 020 and 038), so a first name this admits is one the database admits.
 */
export const FIRST_NAME_SOURCE = `^[${PART_CHARACTERS}]{${MIN_FIRST_NAME_CHARS},${MAX_FIRST_NAME_CHARS}}$`;

/**
 * The first-name rule as a regular expression.
 */
export const FIRST_NAME_PATTERN = new RegExp(FIRST_NAME_SOURCE);

/**
 * Shortest family name accepted.
 */
export const MIN_FAMILY_NAME_CHARS = 3;

/**
 * Longest family name accepted.
 */
export const MAX_FAMILY_NAME_CHARS = 12;

/**
 * The family-name rule's shape as pattern text, anchored. The same text is the `accounts_family_name_shape`
 * check on `accounts.family_name`, so a family name this admits is one the database admits.
 */
export const FAMILY_NAME_SOURCE = `^[${PART_CHARACTERS}]{${MIN_FAMILY_NAME_CHARS},${MAX_FAMILY_NAME_CHARS}}$`;

/**
 * The family-name rule's shape as a regular expression.
 */
export const FAMILY_NAME_PATTERN = new RegExp(FAMILY_NAME_SOURCE);

/**
 * The families whose name itself carries standing in the Story (ADR-065 decision 2, tier 2; ADR-104 R42), which
 * no account may take, ignoring case: a noble house, and the bloodline a plot turns on. "Caelum's family name
 * opens doors" is canon only while the name is the Ashfords' alone. Every other family of the Story may be
 * founded, its characters kept by `RESERVED_FULL_NAMES`. The database does not hold the list, so extending it
 * needs no migration.
 */
export const RESERVED_FAMILY_NAMES: readonly string[] = ["Ashford", "Venn"];

/**
 * The reserved families in lower case, for comparisons that ignore case.
 */
const RESERVED_LOWER: ReadonlySet<string> = new Set(RESERVED_FAMILY_NAMES.map((name) => name.toLowerCase()));

/**
 * A full name: a first name and the family name it stands in.
 */
export interface FullNamePair {
  readonly firstName: string;
  readonly familyName: string;
}

/**
 * The Story's characters who have both names (ADR-065 decision 2, tier 1; ADR-104's table "Tier 1: reserved
 * full names"), whose first name no character may take in a family of their family name, ignoring case: anyone
 * may found the family Cole, but no character in it may be named Garrett. Extended when the Story names
 * another character with both names; the database does not hold the list, so extending it needs no migration.
 */
export const RESERVED_FULL_NAMES: readonly FullNamePair[] = [
  { firstName: "Aldric", familyName: "Venn" },
  { firstName: "Elena", familyName: "Venn" },
  { firstName: "Sera", familyName: "Thorne" },
  { firstName: "Caelum", familyName: "Ashford" },
  { firstName: "Mira", familyName: "Cade" },
  { firstName: "Amara", familyName: "Solinus" },
  { firstName: "Vance", familyName: "Corwin" },
  { firstName: "Theron", familyName: "Val" },
  { firstName: "Colvin", familyName: "March" },
  { firstName: "Garrett", familyName: "Cole" },
];

/**
 * The key a full name is compared by: both parts in lower case, joined by a space, which no part may hold.
 *
 * @param firstName The first name.
 * @param familyName The family name.
 * @returns The key.
 */
function fullNameKey(firstName: string, familyName: string): string {
  return `${firstName.toLowerCase()} ${familyName.toLowerCase()}`;
}

/**
 * The reserved full names as keys, for comparisons that ignore case.
 */
const RESERVED_FULL_LOWER: ReadonlySet<string> = new Set(RESERVED_FULL_NAMES.map((pair) => fullNameKey(pair.firstName, pair.familyName)));

/**
 * The staff words no new first name or family name may hold (ADR-112 decision 5.8, R-0206), in lower case: a name
 * shown beside a chat line or a badge must not pass for a staff member's. Matched by `holdsReservedNameWord`.
 * Names that exist are never judged by it. The database does not hold the list, so extending it needs no migration.
 */
export const RESERVED_NAME_WORDS: readonly string[] = ["admin", "administrator", "moderator", "mod", "gm", "gamemaster", "staff", "support", "guide", "dev", "developer", "official", "odysseytales"];

/**
 * The staff words as a set, for comparisons in lower case.
 */
const RESERVED_WORDS_LOWER: ReadonlySet<string> = new Set(RESERVED_NAME_WORDS);

/**
 * The parts of a name that a word can stand as, `_` and `-` set a part off by matching none of it: a run of capitals not followed by a lower-case letter, a capital or
 * not followed by lower-case letters, or a run of digits, so `ModRook` is `Mod` and `Rook`, `GMTovan` is `GM` and
 * `Tovan`, and `Modesty` is one part.
 */
const NAME_WORD_PATTERN = /[A-Z]+(?![a-z])|[A-Z]?[a-z]+|[0-9]+/g;

/**
 * Reports whether a name holds a staff word, ignoring case: as the whole name, or as a part set off by `_`, `-` or a
 * change of case. A word that merely sits inside a longer one is not held ("Modesty", "Devon", "Staffan").
 *
 * @param name The name as given.
 * @returns True when the name is or has a part that is a staff word.
 */
export function holdsReservedNameWord(name: string): boolean {
  if (RESERVED_WORDS_LOWER.has(name.toLowerCase())) {
    return true;
  }
  for (const word of name.match(NAME_WORD_PATTERN) ?? []) {
    if (RESERVED_WORDS_LOWER.has(word.toLowerCase())) {
      return true;
    }
  }
  return false;
}

/**
 * The fields of the name step's body a naming grant can ask for, in the form's order: the account's family
 * name, then the first name of its first character, which the wire calls `characterName`, as the step before
 * families did.
 */
export const NAME_STEP_FIELDS = ["familyName", "characterName"] as const;

/**
 * One field the name step can ask for.
 */
export type NameStepField = (typeof NAME_STEP_FIELDS)[number];

/**
 * Why a family name is refused: out of the rule's shape, or one the Story keeps whole.
 */
export type FamilyNameProblem = "shape" | "reserved";

/**
 * Why a new first name is refused: out of the rule's shape, or holding a staff word.
 */
export type FirstNameProblem = "shape" | "reserved";

/**
 * Reports whether a name is a first name the rule's shape admits. Uniqueness across the game is the database's to
 * judge. It does not judge staff words, so a name stored before ADR-112 decision 5.8 still passes it wherever a
 * stored name is read; a name being chosen is judged by `firstNameProblem`.
 *
 * @param name The name as given.
 * @returns True when the shape is acceptable.
 */
export function isFirstName(name: string): boolean {
  return FIRST_NAME_PATTERN.test(name);
}

/**
 * Whether a character is one a first name or a family name may hold at some place in it, whatever else the
 * rule asks of a whole name. A search reads this to know what may be typed.
 *
 * @param character One character.
 * @returns True when some name may hold the character.
 */
export function isNameCharacter(character: string): boolean {
  return NAME_CHARACTER_PATTERN.test(character);
}

/**
 * Reports whether a name is a family the Story keeps whole, ignoring case.
 *
 * @param name The name as given.
 * @returns True when the Story keeps it.
 */
export function isReservedFamilyName(name: string): boolean {
  return RESERVED_LOWER.has(name.toLowerCase());
}

/**
 * Reports whether a first name in a family makes the full name of one of the Story's characters, ignoring the
 * case of each part. The check is on the pair: either part is free with any other.
 *
 * @param firstName The first name as given.
 * @param familyName The family name as given, empty when there is none.
 * @returns True when the Story keeps the pair.
 */
export function isReservedFullName(firstName: string, familyName: string): boolean {
  return RESERVED_FULL_LOWER.has(fullNameKey(firstName, familyName));
}

/**
 * Why a new first name would be refused before the database is asked, the shape checked first. Whether another
 * character holds it is the database's to judge.
 *
 * @param name The name as given.
 * @returns `shape` when it breaks the rule's shape, `reserved` when it holds a staff word, or null when it may be
 *   taken.
 */
export function firstNameProblem(name: string): FirstNameProblem | null {
  if (!FIRST_NAME_PATTERN.test(name)) {
    return "shape";
  }
  return holdsReservedNameWord(name) ? "reserved" : null;
}

/**
 * Why a family name would be refused before the database is asked, the shape checked first. Whether another
 * account holds it is the database's to judge.
 *
 * @param name The name as given.
 * @returns `shape` when it breaks the rule's shape, `reserved` when the Story keeps it or it holds a staff word, or
 *   null when it may be claimed.
 */
export function familyNameProblem(name: string): FamilyNameProblem | null {
  if (!FAMILY_NAME_PATTERN.test(name)) {
    return "shape";
  }
  return isReservedFamilyName(name) || holdsReservedNameWord(name) ? "reserved" : null;
}

/**
 * Reports whether a name is a family name an account may claim.
 *
 * @param name The name as given.
 * @returns True when its shape is acceptable, the Story does not keep it and it holds no staff word.
 */
export function isFamilyName(name: string): boolean {
  return familyNameProblem(name) === null;
}

/**
 * The character's name as everyone sees it: the first name, one space and the family name, or the first name
 * alone when there is no family, as for a dev join or a session from before families.
 *
 * @param firstName The first name.
 * @param familyName The family name, or null, undefined or empty when there is none.
 * @returns The full name.
 */
export function fullName(firstName: string, familyName: string | null | undefined): string {
  return familyName === null || familyName === undefined || familyName === "" ? firstName : `${firstName} ${familyName}`;
}

/**
 * The fields a naming grant's `needs` names, in the form's order, each once.
 *
 * @param value The grant's `needs` as parsed from JSON.
 * @returns The fields, or null when the value is not an array or names no field the step knows, which is how
 *   a naming grant from a gateway before families reads.
 */
export function nameStepNeeds(value: unknown): NameStepField[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const named: readonly unknown[] = value;
  const fields = NAME_STEP_FIELDS.filter((field) => named.includes(field));
  return fields.length === 0 ? null : fields;
}
